import {
  nearMissHasWord,
  nearMissMissingFrom,
  nearMissRawWords,
  nearMissSearchWords,
  nearMissStems,
  type NearMissPageResult,
  type NearMissRemovalReason,
  type NearMissSearch
} from './near-miss-searches';
import { stemTopicToken } from './topic-supporting-pages';

/**
 * The "You're close on these searches" card (quality sprint 8, workstream C; NEAR_MISS_SEARCHES.md
 * §1, §3.5). One answer per near-miss search the engine kept (`near-miss-searches.ts`):
 *
 * - **wording**: the page has the search's words, but not where Google weighs them (the title, the
 *   H1, a heading). Put the searcher's phrase in a heading or the opening.
 * - **section**: a word of the search is nowhere on the page. Add a section on it.
 * - **steps**: a how-to search whose task the title or H1 already names, on a page that is not
 *   itself a how-to. Add the steps, as a short how-to section. A kind of section.
 * - **nothing**: the page already carries the words where Google weighs them (an authority
 *   question, not content), or the search asks for something else. Export only ("Checked" lines),
 *   per the owner's rule that no card shows when there is nothing to do (2026-10-19).
 *
 * Never says a change will make the page rank: the card says the page is close (its position and
 * the demand) and what is missing. No model call (owner, 2026-10-19): words and page structure only.
 */

export type NearMissAnswer = 'wording' | 'section' | 'steps' | 'nothing';

/** Why a kept search gets no item on the card. */
export type NearMissNothingReason =
  /** Every word is already in the title, the H1 or one heading. */
  | 'in_heading'
  /** The search ends on a kind of provider (an agency, a tool, an app) the page does not name in its title or H1. */
  | 'different_job'
  /** A comparison question ("difference between", "which is better") on a page that is the comparison. */
  | 'comparison_answered'
  /** The search asks for a file format the page does not offer. */
  | 'format_not_offered'
  /**
   * Every word is somewhere on the page, but no section holds them all: no part of the page is about
   * this search, so a heading would promise what the page does not say. Sprint 8 labels (queue pages,
   * kept): ManyRequests 4 worth doing, 5 maybe, 6 not worth it, 1 noise; SlideSpeak 1 worth doing (a
   * duplicate of a shown search), 15 not worth it (another tool's job: downloading a video, removing
   * a password). Both sites: 21 not worth it and 1 noise kept off the card for 5 worth doing lost;
   * the losses are recorded in TECHNICAL_DEBT §0cm.
   */
  | 'scattered'
  /**
   * The only words the page lacks are another word for one its title or H1 uses: a generic verb
   * ("change" in "how to change X to Y"), an abbreviation ("ms" for a title's "Microsoft") or a
   * common synonym (`NEAR_MISS_SYNONYMS`). A new section for them would repeat what the page says.
   * Round 2 grade: 6 SlideSpeak "add a section" items graded wrong for exactly this.
   */
  | 'synonym_on_page'
  /** Far smaller than the page's main near miss (`NEAR_MISS_CARD_MIN_DEMAND_SHARE`). */
  | 'small';

/**
 * The searcher's last word naming who or what they want instead of an explanation: someone to hire
 * or a product to use. A search that ends on one asks for a provider; on a page whose title or H1
 * does not name that kind of thing it is another page's job. Sprint 8 labels (queue pages, kept
 * searches outside a heading): ManyRequests 8 not worth it and 2 maybe (hire an agency, an AI agent
 * product, the software of the same name), none worth doing; SlideSpeak 1, not worth it. The same
 * word in front of the subject ("<audience> <thing> templates") is the audience, not the thing asked
 * for, and is left alone; so is one after "for" or "types of" ("<thing> for <kind of> agencies",
 * "types of <kind of> agencies"), which asks about them, not for one to hire (round 2 grade).
 */
const PROVIDER_WORDS = new Set([
  'agency', 'agencies', 'firm', 'firms', 'company', 'companies', 'consultant', 'consultants',
  'freelancer', 'freelancers', 'agent', 'agents', 'tool', 'tools', 'software', 'app', 'apps', 'platform', 'platforms'
]);

/** Words before a provider word that make it the subject of the search, not someone to hire. */
const PROVIDER_AS_SUBJECT = /\b(for|types? of|kinds? of)\b/;

/**
 * Verbs a searcher puts in front of the task that say nothing the page must say: "how to change X to
 * Y" asks what the page's "convert X to Y" does.
 */
export const NEAR_MISS_GENERIC_VERBS: readonly string[] = ['change', 'changing', 'turn', 'put', 'do', 'set', 'go', 'switch', 'transform'];

/**
 * Small groups of words searchers use for one another on these pages' subjects. A word the page lacks
 * whose group-mate the title or H1 uses is not missing content. A list, so it only covers what it
 * names: a synonym it lacks still reads as a missing word (TECHNICAL_DEBT §0cm; remaining option: a
 * model check, not taken, owner decision 2026-10-19).
 */
export const NEAR_MISS_SYNONYMS: readonly (readonly string[])[] = [
  ['minimize', 'minimise', 'reduce', 'shrink', 'compress', 'smaller'],
  ['picture', 'pictures', 'image', 'images', 'photo', 'photos'],
  ['delete', 'remove', 'erase', 'clear'],
  ['convert', 'converter', 'conversion', 'change', 'turn', 'switch']
];

/** "ms" for "microsoft": two or three letters, the first of a title word's, all of them in it in order. */
function abbreviates(short: string, word: string): boolean {
  if (short.length < 2 || short.length > 3 || word.length < 5 || word[0] !== short[0]) return false;
  let i = 0;
  for (const ch of word) if (ch === short[i]) i += 1;
  return i === short.length;
}

/** The page already says this word another way: a generic verb, an abbreviation or a synonym of a title or H1 word. */
function saidAnotherWay(word: string, titleWords: ReadonlySet<string>): boolean {
  if (NEAR_MISS_GENERIC_VERBS.includes(word)) return true;
  for (const t of titleWords) if (abbreviates(word, t)) return true;
  return NEAR_MISS_SYNONYMS.some((g) => g.includes(word) && g.some((x) => x !== word && titleWords.has(x)));
}

/** The provider rule: the search ends on a provider word the title and H1 do not name, and not as its subject. */
function asksForProvider(search: string, page: Pick<NearMissPageResult, 'title' | 'h1'>): boolean {
  const words = nearMissSearchWords(search);
  const last = words[words.length - 1];
  if (!last || !PROVIDER_WORDS.has(last)) return false;
  const title = `${page.title ?? ''} ${page.h1 ?? ''}`;
  if (new Set(nearMissRawWords(title)).has(last) || nearMissHasWord(nearMissStems(title), last)) return false;
  return !PROVIDER_AS_SUBJECT.test(nearMissRawWords(search).join(' '));
}

/** Words that turn a search into a question a comparison page ("X vs Y") answers by being one. */
const COMPARISON_WORDS = new Set(['difference', 'differences', 'between', 'better', 'best', 'which', 'compare', 'comparison', 'or']);

/**
 * File formats searchers name, each with the spellings a page may use for the same format. A format
 * the title or H1 names under another spelling is the page's own ("Word" for a page titled "DOCX"):
 * a wording item. A format the title and H1 name under no spelling is not offered: nothing.
 */
const FORMAT_SPELLINGS: Record<string, readonly string[]> = {
  word: ['word', 'docx', 'doc', 'docs'],
  docx: ['word', 'docx', 'doc'],
  doc: ['word', 'docx', 'doc'],
  excel: ['excel', 'xlsx', 'xls', 'sheets'],
  xlsx: ['excel', 'xlsx', 'xls'],
  xls: ['excel', 'xlsx', 'xls'],
  pdf: ['pdf'],
  powerpoint: ['powerpoint', 'ppt', 'pptx', 'slides'],
  ppt: ['powerpoint', 'ppt', 'pptx'],
  pptx: ['powerpoint', 'ppt', 'pptx'],
  csv: ['csv']
};

/** How a format is written for a reader: a product's name, or an extension in capitals. */
const FORMAT_DISPLAY: Record<string, string> = {
  word: 'Word',
  excel: 'Excel',
  powerpoint: 'PowerPoint',
  sheets: 'Sheets',
  slides: 'Slides',
  docs: 'Docs'
};
const formatDisplay = (f: string): string => FORMAT_DISPLAY[f] ?? f.toUpperCase();

const HOW_TO = /^(how to|how do i|how do you|how can i|how can you|how)\b/;
/**
 * A section heading that is itself a how-to: on a tool page, the one that says how to use the tool.
 * An FAQ question ("How do I …?") is not one: its answer is a paragraph, not a section to add steps to.
 */
const HOW_TO_HEADING = /^how to\b[^?]*$/i;

/**
 * A search with less demand than this share of the page's biggest near miss gets no item ('small'):
 * the writer's change is better spent on the page's main near misses, and a card of every small
 * variant buries them. "Biggest" leaves out a search already in a heading and a format the page does
 * not offer (round 2 grade: a tool's own head term in its title cut every how-to of the page).
 * Sprint 8 labelled set (queue pages; searches that would otherwise get an item): ManyRequests under
 * the cut 16 not worth it, 6 maybe, 2 noise, no worth doing (the lowest sits at 0.29); SlideSpeak 2
 * not worth it, 1 maybe, 1 worth doing (a duplicate, at 0.16). One rule for both sites.
 */
export const NEAR_MISS_CARD_MIN_DEMAND_SHARE = 0.25;

/** One kept search's answer, and for "nothing" why. */
export interface NearMissSearchAnswer {
  answer: NearMissAnswer;
  reason: NearMissNothingReason | null;
  /** Content words of the search the title and H1 do not carry (wording, steps). */
  notInTitle: string[];
  /** Words nowhere on the page (section). */
  missingWords: string[];
  /** For a format named under another spelling: the spelling the title uses. */
  formatSpelling: { search: string; page: string } | null;
  /** For 'in_heading': where the words already are. */
  heading: { kind: 'title' | 'h1' | 'heading'; text: string } | null;
}

function titleStems(result: Pick<NearMissPageResult, 'title' | 'h1'>): Set<string> {
  return nearMissStems(`${result.title ?? ''} ${result.h1 ?? ''}`);
}

/** The answer for one kept search on a page whose title and H1 are given. Pure. */
export function nearMissSearchAnswer(s: NearMissSearch, page: Pick<NearMissPageResult, 'title' | 'h1'>): NearMissSearchAnswer {
  const loc = s.location;
  const words = nearMissSearchWords(s.search);
  const inTitle = titleStems(page);
  const notInTitle = nearMissMissingFrom(inTitle, words);
  const base: NearMissSearchAnswer = {
    answer: 'nothing',
    reason: null,
    notInTitle,
    missingWords: loc.missingWords,
    formatSpelling: null,
    heading: null
  };
  const raw = nearMissRawWords(s.search);
  const titleRaw = new Set(nearMissRawWords(`${page.title ?? ''} ${page.h1 ?? ''}`));

  // A how-to search whose task the title or H1 names, on a page that is not itself a how-to.
  const howTo = HOW_TO.test(raw.join(' ')) && !titleRaw.has('how');

  if (loc.place === 'heading') {
    if (howTo && loc.heading && loc.heading.kind !== 'heading') return { ...base, answer: 'steps' };
    return { ...base, reason: 'in_heading', heading: loc.heading };
  }

  if (asksForProvider(s.search, page)) return { ...base, reason: 'different_job' };

  if ((titleRaw.has('vs') || titleRaw.has('versus')) && raw.some((w) => COMPARISON_WORDS.has(w))) {
    return { ...base, reason: 'comparison_answered' };
  }

  if (notInTitle.length && notInTitle.every((w) => FORMAT_SPELLINGS[w])) {
    const spellings = notInTitle.map((w) => ({ search: w, page: FORMAT_SPELLINGS[w].find((f) => titleRaw.has(f)) }));
    const offered = spellings.find((x) => x.page);
    if (!offered || spellings.some((x) => !x.page)) return { ...base, reason: 'format_not_offered' };
    // The format is the page's own, so the task is the title's: a how-to of it asks for the steps.
    if (howTo) return { ...base, answer: 'steps' };
    return { ...base, answer: 'wording', formatSpelling: { search: offered.search, page: offered.page! } };
  }

  if (loc.place === 'not_on_page') {
    if (loc.missingWords.every((w) => saidAnotherWay(w, titleRaw))) {
      // Every other word is in the title: a how-to of the title's task asks for the steps.
      const rest = notInTitle.filter((w) => !saidAnotherWay(w, titleRaw));
      if (howTo && !rest.length) return { ...base, answer: 'steps' };
      return { ...base, reason: 'synonym_on_page' };
    }
    return { ...base, answer: 'section' };
  }
  if (loc.place === 'spread') return { ...base, reason: 'scattered' };
  return { ...base, answer: 'wording' };
}

/** One search an item lists, with its numbers. */
export interface NearMissItemSearch {
  search: string;
  position: number;
  impressions: number | null;
  clicks: number | null;
  searchVolume: number | null;
  demand: number;
}

/** One item on the card: one change, for one search or several of one intent. */
export interface NearMissCardItem {
  answer: Exclude<NearMissAnswer, 'nothing'>;
  /** Most demand first; the first is the one the change is worded on. */
  searches: NearMissItemSearch[];
  /** The searches' demand added up: what the card is ordered by. */
  demand: number;
  /** Short label for the change. */
  label: string;
  /** The change, one sentence. */
  change: string;
  /** What is missing, one sentence. */
  why: string;
}

/** A search the card checked and gives no item: the everything export lists it. */
export interface NearMissCheckedLine {
  search: string;
  /** The engine's noise rule, or the card's reason. */
  reason: NearMissRemovalReason | NearMissNothingReason;
  numbers: string;
  sentence: string;
}

export interface NearMissCardView {
  estimated: boolean;
  /** `ESTIMATED_SEARCH_EVIDENCE_WARNING` without Search Console; null with it. */
  warning: string | null;
  items: NearMissCardItem[];
}

export const NEAR_MISS_CARD_TITLE = "You're close on these searches";

const quote = (words: readonly string[]): string => words.map((w) => `"${w}"`).join(', ');

/** "position 8.3" (Search Console, one decimal) or "estimated position 8". */
export function nearMissPositionLabel(s: Pick<NearMissItemSearch, 'position'>, estimated: boolean): string {
  return estimated ? `estimated position ${Math.round(s.position)}` : `average position ${s.position.toFixed(1)}`;
}

/** The numbers a search rests on, one phrase. */
export function nearMissNumbersLabel(s: NearMissItemSearch, estimated: boolean): string {
  if (estimated) {
    return `${nearMissPositionLabel(s, true)}, about ${(s.searchVolume ?? 0).toLocaleString('en-US')} searches a month`;
  }
  const clicks = s.clicks ?? 0;
  return `${(s.impressions ?? 0).toLocaleString('en-US')} impressions, ${clicks.toLocaleString('en-US')} ${
    clicks === 1 ? 'click' : 'clicks'
  }, ${nearMissPositionLabel(s, false)} over 90 days`;
}

function itemSearch(s: NearMissSearch): NearMissItemSearch {
  return {
    search: s.search,
    position: s.position,
    impressions: s.impressions,
    clicks: s.clicks,
    searchVolume: s.searchVolume,
    demand: s.demand
  };
}

/**
 * The words that decide one change: the change is the same when these overlap. Steps: one per page
 * (the title's task). A format: the format. Wording: the words the title and H1 lack. A section: the
 * words the page lacks. Round 2 grade: 13 of 29 wrong items repeated an earlier item's change.
 */
function changeWords(a: NearMissSearchAnswer, s: NearMissSearch): string[] {
  if (a.answer === 'steps') return ['steps'];
  if (a.formatSpelling) return [`format:${a.formatSpelling.search}`];
  const words = [...(a.answer === 'section' ? a.missingWords : a.notInTitle)];
  // Sections for kinds of one thing ("<kind> <subject>" for several kinds the page never names) are
  // one section: the words the page has are the subject, the same for each.
  if (a.answer === 'section') {
    const missing = new Set(words.map(stemTopicToken));
    const rest = nearMissSearchWords(s.search).map(stemTopicToken).filter((w) => !missing.has(w));
    if (rest.length) words.push(`=${[...new Set(rest)].sort().join(' ')}`);
  }
  // Nothing missing from the title (its words are there, apart): the search's own words, whole.
  if (!words.length) return [`${a.answer}=${[...new Set(nearMissSearchWords(s.search).map(stemTopicToken))].sort().join(' ')}`];
  return words.map((w) => (w.startsWith('=') ? w : `${a.answer}:${w}`));
}

/**
 * Two change words are one when they are the same key, or the same answer's words of one family
 * ("calculate" and "calculation"), as the location check reads words (round 3 grade).
 */
function sameChangeWord(x: string, y: string): boolean {
  if (x === y) return true;
  const [ax, wx] = x.split(':');
  const [ay, wy] = y.split(':');
  if (wx === undefined || wy === undefined || ax !== ay || ax === 'format') return false;
  return nearMissHasWord(nearMissStems(wx), wy);
}

/** Every distinct word of a list of lists, in first-seen order, one per word family. */
function unionWords(lists: readonly (readonly string[])[]): string[] {
  const out: string[] = [];
  for (const list of lists) {
    for (const w of list) {
      if (out.some((o) => nearMissHasWord(nearMissStems(o), w))) continue;
      out.push(w);
    }
  }
  return out;
}

function itemText(
  a: NearMissSearchAnswer,
  lead: string,
  group: readonly NearMissSearchAnswer[],
  howToHeading: string | null
): { label: string; change: string; why: string } {
  if (a.answer === 'steps') {
    return howToHeading
      ? {
          label: 'HOW-TO STEPS',
          change: `Under "${howToHeading}", add the steps done in the app itself, in the searcher's words ("${lead}").`,
          why: "Searchers ask how to do this themselves; the page's how-to heading is about using this tool."
        }
      : {
          label: 'HOW-TO STEPS',
          change: `Add a short how-to section that gives the steps, headed in the searcher's words ("${lead}").`,
          why: 'Searchers ask how to do this themselves; the title or H1 names the task, but the page is not written as a how-to.'
        };
  }
  if (a.answer === 'section') {
    return {
      label: 'NEW SECTION',
      change: `Add a section that answers "${lead}".`,
      why: `The page never says ${quote(unionWords(group.map((g) => g.missingWords)))}.`
    };
  }
  if (a.formatSpelling) {
    return {
      label: 'WORDING',
      change: `Name "${formatDisplay(a.formatSpelling.search)}" in the title or H1, as searchers do.`,
      why: `The title says "${formatDisplay(a.formatSpelling.page)}"; searchers say "${formatDisplay(a.formatSpelling.search)}".`
    };
  }
  const missing = unionWords(group.map((g) => g.notInTitle));
  return {
    label: 'WORDING',
    change: `Use the searcher's phrase "${lead}" in a heading or the opening.`,
    why: missing.length
      ? `The page has every word of it, but ${quote(missing)} ${missing.length === 1 ? 'is' : 'are'} not in the title or H1.`
      : 'The page has every word of it, but never together in the title, the H1 or one heading.'
  };
}

function checkedSentence(s: NearMissSearch, reason: NearMissRemovalReason | NearMissNothingReason, a: NearMissSearchAnswer | null): string {
  switch (reason) {
    case 'in_heading': {
      const where = a?.heading?.kind === 'title' ? 'the title' : a?.heading?.kind === 'h1' ? 'the H1' : 'a heading';
      return `Its words are already in ${where}${a?.heading ? ` ("${a.heading.text}")` : ''}: being at this position is about the page's strength, not missing words.`;
    }
    case 'different_job':
      return 'It asks for a provider or a product, not what this page offers.';
    case 'comparison_answered':
      return 'A comparison question this comparison page answers by being one.';
    case 'format_not_offered':
      return 'It asks for a file format this page does not offer.';
    case 'synonym_on_page': {
      const words = a?.missingWords ?? [];
      return words.length === 1
        ? `The only word the page lacks, ${quote(words)}, says in other words what the page already says.`
        : `The only words the page lacks, ${quote(words)}, say in other words what the page already says.`;
    }
    case 'scattered':
      return 'Its words are on the page, but no part of the page is about it.';
    case 'small':
      return "Small next to this page's main near misses.";
    case 'spam':
      return 'Not a person searching for this page (an operator, another site, or a string of keywords).';
    case 'ai_prompt':
      return 'A long prompt typed into an AI assistant, not a search.';
    case 'other_language':
      return 'Not in English.';
    case 'year_variant':
      return 'A dated variant: the year is not the one on the page.';
    case 'place_variant':
      return 'A local variant: the place is not named on the page.';
    case 'title_match':
      return 'The title or H1 already says it word for word: being at this position is about the page\'s strength, not missing words.';
    case 'answered_variant':
      return 'A "sample / template / free" variant of what the title or H1 already says word for word.';
    case 'shared_with_our_page':
      return 'Another page of your site shows for it too; the Cannibalization check owns it.';
  }
}

/** Every near-miss search of the page, answered: the card's items and the export's "Checked" lines. */
export function nearMissCard(result: NearMissPageResult): { view: NearMissCardView; checked: NearMissCheckedLine[] } {
  const answers = new Map<NearMissSearch, NearMissSearchAnswer>();
  for (const s of result.searches) if (s.kept) answers.set(s, nearMissSearchAnswer(s, result));
  // Measured against the biggest search that would itself get an item, not a title match or a
  // format the page does not offer (round 2 grade: a tool's own head term cut every how-to).
  const top = [...answers].reduce(
    (m, [s, a]) => (a.reason === 'in_heading' || a.reason === 'format_not_offered' ? m : Math.max(m, s.demand)),
    0
  );
  const checked: NearMissCheckedLine[] = [];
  const groups: Array<{ words: Set<string>; answers: NearMissSearchAnswer[]; searches: NearMissSearch[] }> = [];
  const howToHeading = (result.headings ?? []).find((h) => HOW_TO_HEADING.test(h)) ?? null;

  for (const s of result.searches) {
    const numbers = nearMissNumbersLabel(itemSearch(s), result.estimated);
    if (!s.kept) {
      const reason = s.reason as NearMissRemovalReason;
      checked.push({ search: s.search, reason, numbers, sentence: checkedSentence(s, reason, null) });
      continue;
    }
    let a = answers.get(s)!;
    if (a.answer !== 'nothing' && top > 0 && s.demand < top * NEAR_MISS_CARD_MIN_DEMAND_SHARE) {
      a = { ...a, answer: 'nothing', reason: 'small' };
    }
    if (a.answer === 'nothing') {
      checked.push({ search: s.search, reason: a.reason!, numbers, sentence: checkedSentence(s, a.reason!, a) });
      continue;
    }
    // One item per change: a search joins every group whose change words it shares, merging them.
    const words = changeWords(a, s);
    const hits = groups.filter((g) => words.some((w) => [...g.words].some((x) => sameChangeWord(w, x))));
    const merged = { words: new Set(words), answers: [a], searches: [s] };
    for (const g of hits) {
      g.words.forEach((w) => merged.words.add(w));
      merged.answers.unshift(...g.answers);
      merged.searches.unshift(...g.searches);
      groups.splice(groups.indexOf(g), 1);
    }
    // The item is worded on the search with the most demand, but never on one that asks to hire a
    // provider (it ends on one; the provider rule holds such searches when they stand alone).
    const hiring = (x: NearMissSearch) => (asksForProvider(x.search, result) ? 1 : 0);
    merged.searches.sort((x, y) => hiring(x) - hiring(y) || y.demand - x.demand);
    groups.push(merged);
  }

  const items: NearMissCardItem[] = groups.map(({ answers: group, searches }) => {
    // The change is worded on the search with the most demand; the group's answers say what is missing.
    const lead = answers.get(searches[0])!;
    const text = itemText(lead, searches[0].search, group, howToHeading);
    return {
      answer: lead.answer as NearMissCardItem['answer'],
      searches: searches.map(itemSearch),
      demand: searches.reduce((n, s) => n + s.demand, 0),
      ...text
    };
  });
  items.sort((x, y) => y.demand - x.demand || (x.searches[0].search < y.searches[0].search ? -1 : 1));
  return { view: { estimated: result.estimated, warning: result.warning, items }, checked };
}
